import { fetchDevServerStatus } from "@/store/codebaseSlice";
import { checkRedisHealth } from "@/utils/checkRedisHealth";
import {
  AlertDialog,
  AlertDialogTitle,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "./ui/alert-dialog";

import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useCanvasId } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

const ServerDownModal = () => {
  const [countdown, setCountdown] = useState(5);
  const [canRefresh, setCanRefresh] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const dispatch = useDispatch<any>();
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const canvasId = useCanvasId();
  const { session } = useSession();
  const [checkingServerStatus, setCheckingServerStatus] = useState(false);

  // Calculate backoff time based on retry count (exponential backoff)
  const getBackoffTime = useCallback(() => {
    // Start with 5 seconds, then double each time with a max of 60 seconds
    return Math.min(10 * Math.pow(2, retryCount), 60);
  }, [retryCount]);

  const checkServerStatus = useCallback(async () => {
    if (!session || !supabaseClient) {
      return;
    }

    setCheckingServerStatus(true);

    const { confirmedRedisDown } = await checkRedisHealth(
      canvasId,
      supabaseClient,
    );
    if (!confirmedRedisDown) {
      // If Redis is up, try to fetch the dev server status
      dispatch(
        fetchDevServerStatus({
          canvasId,
          session,
          supabaseClient,
        }),
      );
    } else {
      setRetryCount((prev) => prev + 1);
      setCountdown(getBackoffTime());
    }
    setCheckingServerStatus(false);
  }, [canvasId, Boolean(session), getBackoffTime, Boolean(supabaseClient)]);

  useEffect(() => {
    // When the countdown ends, check the server status and set to a new countdown
    if (countdown === 0) {
      checkServerStatus();

      // Alow refreshing after the first countodwn ends
      setCanRefresh(true);

      // Update countdown
      const newBackoffTime = getBackoffTime();
      setCountdown(newBackoffTime);
    }
  }, [countdown]);

  // Add a new useEffect that handles the actual countdown logic but doesn't depend on countdown itself
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prevCount) => {
        if (prevCount <= 1) {
          // Don't count below 0
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <AlertDialog open={true}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>We'll be back</AlertDialogTitle>
          <AlertDialogDescription>
            Tempo is down temporarily. We are scaling up our servers and will be
            back soon. Please come back in a few minutes.{" "}
            {countdown > 0 && !checkingServerStatus && (
              <span className="text-sm text-gray-500">
                Checking again in {countdown}s
              </span>
            )}
            {checkingServerStatus && (
              <span className="text-sm text-gray-500">
                Checking server status...
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              window.location.reload();
            }}
            disabled={!canRefresh}
          >
            Refresh
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ServerDownModal;
